/**
 * 정산 수수료 목록 화면.
 * admin-ruda-web `settlementFeeInfo.js` 포팅 (목록·검색·배치 수정·업로드·엑셀).
 * 액션 버튼 구성·라벨은 레거시와 동일하게 유지한다.
 */
import {
  isCommissionExportEmptyError,
  type CommissionBatchUpdateItem,
} from '@/entities/commission/api/commission.api';
import {
  useBatchUpdateCommissions,
  useCommissionList,
  useExportCommissions,
} from '@/entities/commission/hooks/useCommissions';
import { useUploadCommissionExcel } from '@/entities/drug-data/hooks/useDrugData';
import {
  formatPrice,
  getDefaultRefYm,
  getPreviousMonths,
} from '@/pages/drugs/drugListUtils';
import {
  InvalidDrugCodesModal,
  InvalidPharmaDrugCodesModal,
  UnknownPharmaModal,
} from '@/pages/settlement-fees/CommissionValidationModals';
import {
  COMMISSION_BATCH_HEADER_MAP,
  COMMISSION_BATCH_UPLOAD_ACCEPT,
  parseCommissionBatchUploadFile,
} from '@/pages/settlement-fees/commissionBatchUtils';
import {
  COMMISSION_PROCESSED_FILE_NAME,
  COMMISSION_UPLOAD_ACCEPT,
  validateCommissionUploadFile,
  type CommissionUploadValidationResult,
} from '@/pages/settlement-fees/commissionUploadValidation';
import { formatCommissionRateDisplay } from '@/pages/settlement-fees/commissionDetailUtils';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { FilterRow, Stack, ToolbarRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  ROUTE_PATHS,
  settlementFeeDetailPath,
} from '@/shared/constants/routes';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { openPresignedFile } from '@/shared/utils/openPresignedFile';
import { exportXlsxTemplate } from '@/shared/utils/xlsxExport';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate } from '@tanstack/react-router';
import { Banknote, Download, Upload } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const SETTLEMENT_FEE_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  referenceYearMonth: getDefaultRefYm(),
  pharmaCompanyName: '',
  drugName: '',
  drugCode: '',
};

export const SettlementFeesPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(
    SETTLEMENT_FEE_SEARCH_DEFAULTS,
  );

  const [pharmaNameInput, setPharmaNameInput] = useState(
    values.pharmaCompanyName,
  );
  const [drugNameInput, setDrugNameInput] = useState(values.drugName);
  const [drugCodeInput, setDrugCodeInput] = useState(values.drugCode);

  useEffect(() => {
    setPharmaNameInput(values.pharmaCompanyName);
    setDrugNameInput(values.drugName);
    setDrugCodeInput(values.drugCode);
  }, [values.pharmaCompanyName, values.drugName, values.drugCode]);

  const search = useMemo(
    () => ({
      referenceYearMonth: values.referenceYearMonth,
      pharmaCompanyName: values.pharmaCompanyName || undefined,
      drugName: values.drugName || undefined,
      drugCode: values.drugCode || undefined,
    }),
    [values],
  );

  const pageable = useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchItems, setBatchItems] = useState<CommissionBatchUpdateItem[]>([]);
  const [batchFileName, setBatchFileName] = useState('');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadValidating, setUploadValidating] = useState(false);
  const [invalidDrugCodes, setInvalidDrugCodes] = useState<string[]>([]);
  const [invalidPharmaDrugCodes, setInvalidPharmaDrugCodes] = useState<
    string[]
  >([]);
  const [unknownPharmaNames, setUnknownPharmaNames] = useState<string[]>([]);
  const [unmatchedPharmaDrugCodes, setUnmatchedPharmaDrugCodes] = useState<
    string[]
  >([]);
  const [invalidDrugModalOpen, setInvalidDrugModalOpen] = useState(false);
  const [invalidPharmaModalOpen, setInvalidPharmaModalOpen] = useState(false);
  const [unknownPharmaModalOpen, setUnknownPharmaModalOpen] = useState(false);

  const batchUpdate = useBatchUpdateCommissions();
  const uploadCommission = useUploadCommissionExcel();
  const exportMutation = useExportCommissions();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const isExporting = exportMutation.isPending;

  const { data, isLoading, isError, error, refetch } = useCommissionList({
    search,
    pageable,
  });

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  useEffect(() => {
    setGlobalLoading(isExporting, '정산 수수료 목록 엑셀을 준비하는 중...');
    return () => setGlobalLoading(false);
  }, [isExporting, setGlobalLoading]);

  const fees = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasRefYm = Boolean(values.referenceYearMonth);

  // 레거시 SearchFilter: 최근 4개월 + URL에 있는 기준월이 범위 밖이면 옵션에 포함
  const yearMonthOptions = useMemo(() => {
    const toYearMonthLabel = (value: string) => {
      const digits = value.replace(/\D/g, '');
      if (digits.length !== 6) return value;
      return `${digits.slice(0, 4)}.${digits.slice(4, 6)}`;
    };
    const options = getPreviousMonths(4).map((month) => ({
      label: toYearMonthLabel(month),
      value: month.replace('-', ''),
    }));
    const current = values.referenceYearMonth || getDefaultRefYm();
    if (current && !options.some((opt) => opt.value === current)) {
      options.unshift({ label: toYearMonthLabel(current), value: current });
    }
    return options;
  }, [values.referenceYearMonth]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({
      pharmaCompanyName: pharmaNameInput.trim(),
      drugName: drugNameInput.trim(),
      drugCode: drugCodeInput.trim(),
      page: 1,
    });
  };

  const handleBatchFile = async (file: File) => {
    try {
      const items = await parseCommissionBatchUploadFile(file);
      setBatchItems(items);
      setBatchFileName(file.name);
    } catch (e) {
      handleApiError(e, toast);
      setBatchItems([]);
      setBatchFileName('');
    }
  };

  const handleUploadValidation = (
    validation: CommissionUploadValidationResult,
  ): boolean => {
    if (validation.valid) return true;

    if (validation.reason === 'invalid_drug_code_length') {
      setInvalidDrugCodes(validation.invalidCodes ?? []);
      setInvalidDrugModalOpen(true);
      return false;
    }
    if (validation.reason === 'invalid_pharma_company_id') {
      setInvalidPharmaDrugCodes(validation.invalidDrugCodes ?? []);
      setInvalidPharmaModalOpen(true);
      return false;
    }
    if (validation.reason === 'unknown_pharma_company') {
      setUnknownPharmaNames(validation.unknownPharmaNames ?? []);
      setUnmatchedPharmaDrugCodes(validation.unmatchedDrugCodes ?? []);
      setUnknownPharmaModalOpen(true);
      return false;
    }
    if (validation.reason === 'year_month_mismatch') {
      const details = (validation.mismatchDetails ?? [])
        .map((d) => `${d.drugCode} — 파일 년월: ${d.fileYearMonth}`)
        .join(', ');
      toast.error(
        details
          ? `기준월(${validation.expectedReferenceYearMonth})과 다른 년월: ${details}`
          : '기준월과 다른 년월이 있는 행이 있습니다.',
      );
      return false;
    }
    if (validation.reason === 'missing_expected_year_month') {
      toast.error('기준년월을 먼저 선택해 주세요.');
      return false;
    }

    toast.error('파일 형식이 올바르지 않습니다.');
    return false;
  };

  const handleUploadFileSelect = async (file: File) => {
    setUploadValidating(true);
    try {
      const validation = await validateCommissionUploadFile(file, {
        expectedReferenceYearMonth:
          values.referenceYearMonth || getDefaultRefYm(),
      });
      if (!handleUploadValidation(validation)) {
        setUploadFile(null);
        return;
      }
      setUploadFile(
        validation.valid ? (validation.processedFile ?? file) : null,
      );
    } catch (e) {
      handleApiError(e, toast);
      setUploadFile(null);
    } finally {
      setUploadValidating(false);
    }
  };

  const handleCommissionUpload = async () => {
    if (!uploadFile) {
      toast.error('업로드할 파일을 선택해 주세요.');
      return;
    }
    try {
      await uploadCommission.mutateAsync(uploadFile);
      toast.success('정산수수료 업로드를 요청했습니다.');
      setUploadOpen(false);
      setUploadFile(null);
      await refetch();
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDownloadPreparedFile = () => {
    if (!uploadFile) {
      toast.error('다운로드할 파일을 먼저 선택해주세요.');
      return;
    }
    try {
      const url = URL.createObjectURL(uploadFile);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = COMMISSION_PROCESSED_FILE_NAME;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success('파일을 다운로드했습니다.');
    } catch {
      toast.error('다운로드에 실패했습니다.');
    }
  };

  const handleBatchSubmit = async () => {
    if (batchItems.length === 0) {
      toast.error('업로드할 데이터가 없습니다.');
      return;
    }
    try {
      await batchUpdate.mutateAsync(batchItems);
      toast.success(`수수료 정보 ${batchItems.length}건을 수정했습니다.`);
      setBatchOpen(false);
      setBatchItems([]);
      setBatchFileName('');
      await refetch();
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDownloadCommissionsExcel = async () => {
    const ym = String(values.referenceYearMonth ?? '')
      .replace(/-/g, '')
      .trim();
    if (!ym) {
      toast.error('기준월을 먼저 선택해주세요.');
      return;
    }

    try {
      const archive = await exportMutation.mutateAsync({
        referenceYearMonth: ym,
        pharmaCompanyName: values.pharmaCompanyName || undefined,
        drugName: values.drugName || undefined,
        drugCode: values.drugCode || undefined,
      });
      if (!archive.downloadUrl) {
        toast.info('다운로드할 정산 수수료가 없습니다.');
        return;
      }
      openPresignedFile(archive.downloadUrl);
      toast.success(
        `정산 수수료 목록을 다운로드했습니다. (${archive.rowCount.toLocaleString()}건)`,
      );
    } catch (e) {
      if (isCommissionExportEmptyError(e)) {
        toast.info('다운로드할 정산 수수료가 없습니다.');
        return;
      }
      handleApiError(e, toast);
    }
  };

  const resetBatchModal = () => {
    setBatchOpen(false);
    setBatchItems([]);
    setBatchFileName('');
  };

  const batchOutOfStockCount = batchItems.filter(
    (item) => item.outOfStock,
  ).length;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Banknote}
        title="정산수수료 정보 관리"
        description="정산 수수료 목록 조회 및 업로드"
        actions={
          <>
            <MenuWriteGate>
              <Button
                type="button"
                variant="outline"
                onClick={() => setBatchOpen(true)}
              >
                수수료 정보 배치 수정
              </Button>
            </MenuWriteGate>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate({ to: ROUTE_PATHS.COMMISSION_AGGREGATOR } as never)
              }
            >
              수수료 정보 종합
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleDownloadCommissionsExcel()}
              disabled={
                !hasRefYm || isExporting || isLoading || totalCount === 0
              }
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? '처리 중...' : '수수료 목록 엑셀'}
            </Button>
            <MenuWriteGate>
              <Button
                type="button"
                className="ruda-button"
                disabled={!hasRefYm}
                onClick={() => setUploadOpen(true)}
              >
                정산수수료 업로드
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <FilterCard>
        {/* 레거시 SearchFilter: 년월 드롭다운 → 입력(placeholder) + 검색 */}
        <Stack as="form" gap={16} onSubmit={handleSearch}>
          <Select
            value={values.referenceYearMonth || getDefaultRefYm()}
            onValueChange={(next) =>
              setValues({
                referenceYearMonth: next || getDefaultRefYm(),
                page: 1,
              })
            }
          >
            <SelectTrigger className="w-44" aria-label="기준년월">
              <SelectValue placeholder="년월 선택" />
            </SelectTrigger>
            <SelectContent>
              {yearMonthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <FilterRow as="div" gap={12}>
            <div className="space-y-1">
              <Label htmlFor="fee-pharma">제약사명</Label>
              <Input
                id="fee-pharma"
                placeholder="제약사명"
                value={pharmaNameInput}
                onChange={(e) => setPharmaNameInput(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fee-drug-name">의약품명</Label>
              <Input
                id="fee-drug-name"
                placeholder="의약품명"
                value={drugNameInput}
                onChange={(e) => setDrugNameInput(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fee-drug-code">의약품 코드</Label>
              <Input
                id="fee-drug-code"
                placeholder="의약품 코드"
                value={drugCodeInput}
                onChange={(e) => setDrugCodeInput(e.target.value)}
                className="w-40"
              />
            </div>
            <Button type="submit" className="ruda-button mt-6">
              검색
            </Button>
          </FilterRow>
        </Stack>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <div className="py-16">
            <LoadingSpinner tip="정산 수수료 목록을 불러오는 중..." />
          </div>
        ) : fees.length === 0 ? (
          <EmptyState description="검색 조건에 맞는 정산 수수료가 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>기준 년 월</TableHead>
                  <TableHead>제약사명</TableHead>
                  <TableHead>의약품명</TableHead>
                  <TableHead>의약품 코드</TableHead>
                  <TableHead>약가</TableHead>
                  <TableHead>표준수당율</TableHead>
                  <TableHead>표준수당율 메모</TableHead>
                  <TableHead>인센티브</TableHead>
                  <TableHead>인센티브 메모</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fees.map((fee) => (
                  <TableRow
                    key={fee.drugCode}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate({
                        to: settlementFeeDetailPath(fee.drugCode),
                        search: { refYm: fee.referenceYearMonth },
                      } as never)
                    }
                  >
                    <TableCell>{fee.referenceYearMonth}</TableCell>
                    <TableCell>{fee.pharmaCompanyName}</TableCell>
                    <TableCell className="font-medium">
                      {fee.drugName}
                    </TableCell>
                    <TableCell>{fee.drugCode}</TableCell>
                    <TableCell>{formatPrice(fee.price)}</TableCell>
                    <TableCell>
                      {formatCommissionRateDisplay(fee.standardCommissionRate)}
                    </TableCell>
                    <TableCell>{fee.standardCommissionNote || '-'}</TableCell>
                    <TableCell>
                      {fee.incentive != null
                        ? fee.incentive.toLocaleString('ko-KR')
                        : '-'}
                    </TableCell>
                    <TableCell>{fee.incentiveNote || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>

      <Dialog
        open={batchOpen}
        onOpenChange={(open) => {
          if (!open && batchUpdate.isPending) return;
          if (!open) resetBatchModal();
          else setBatchOpen(true);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>수수료 정보 배치 수정</DialogTitle>
            <DialogDescription>
              필수 헤더(년월, 제품코드, 보험가, 제약사수수료율)가 포함된
              CSV·엑셀 파일만 업로드할 수 있으며, 각 행의 필수 열 값도 함께
              검증합니다. `품절여부` 열이 없으면 전체 행이 미품절로 처리되며,
              열이 있더라도 값이 `1`인 행만 품절로 반영됩니다.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ToolbarRow gap={8}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  exportXlsxTemplate(
                    COMMISSION_BATCH_HEADER_MAP,
                    '수수료_배치수정_양식.xlsx',
                  )
                }
              >
                <Download className="mr-2 h-4 w-4" />
                양식 다운로드
              </Button>
              <FileUploadButton
                accept={COMMISSION_BATCH_UPLOAD_ACCEPT}
                onFilesSelected={(files) => {
                  const file = files?.[0];
                  if (file) void handleBatchFile(file);
                }}
              >
                파일 선택
              </FileUploadButton>
            </ToolbarRow>
            {batchItems.length > 0 && (
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>
                  {batchFileName ? `${batchFileName} — ` : ''}
                  수정 대상: {batchItems.length}건
                </p>
                <p>
                  품절 {batchOutOfStockCount}건 / 미품절{' '}
                  {batchItems.length - batchOutOfStockCount}건
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={resetBatchModal}
              disabled={batchUpdate.isPending}
            >
              취소
            </Button>
            <Button
              className="ruda-button"
              onClick={() => void handleBatchSubmit()}
              disabled={batchItems.length === 0 || batchUpdate.isPending}
            >
              수정 반영
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={uploadOpen}
        onOpenChange={(open) => {
          if (!open && uploadCommission.isPending) return;
          setUploadOpen(open);
          if (!open) setUploadFile(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>정산수수료 업로드</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">
              기준년월:{' '}
              <strong>{values.referenceYearMonth || getDefaultRefYm()}</strong>
            </p>
            <FileUploadButton
              accept={COMMISSION_UPLOAD_ACCEPT}
              disabled={uploadValidating || uploadCommission.isPending}
              onFilesSelected={(files) => {
                const file = files?.[0];
                if (file) void handleUploadFileSelect(file);
              }}
            >
              파일 선택
            </FileUploadButton>
            {uploadFile && (
              <p className="text-sm text-muted-foreground">
                선택됨: {uploadFile.name}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadOpen(false);
                setUploadFile(null);
              }}
              disabled={uploadCommission.isPending}
            >
              취소
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadPreparedFile}
              disabled={
                !uploadFile || uploadValidating || uploadCommission.isPending
              }
            >
              <Download />
              변환 파일 다운로드
            </Button>
            <Button
              className="ruda-button"
              onClick={() => void handleCommissionUpload()}
              disabled={!uploadFile || uploadCommission.isPending}
            >
              <Upload />
              업로드
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvalidDrugCodesModal
        open={invalidDrugModalOpen}
        codes={invalidDrugCodes}
        onClose={() => setInvalidDrugModalOpen(false)}
      />
      <InvalidPharmaDrugCodesModal
        open={invalidPharmaModalOpen}
        drugCodes={invalidPharmaDrugCodes}
        onClose={() => setInvalidPharmaModalOpen(false)}
      />
      <UnknownPharmaModal
        open={unknownPharmaModalOpen}
        pharmaNames={unknownPharmaNames}
        drugCodes={unmatchedPharmaDrugCodes}
        onClose={() => setUnknownPharmaModalOpen(false)}
      />
    </div>
  );
};
